/**
 * Capability stocks by tech theme (07.4, `capability.stock@1`).
 *   07.4 E1  K_θ(m) = (1 − d)·K_θ(m−1) + TI_θ(m − L) ÷ 12; before SimStart TI = T0·w; K0 = T0·w ÷ (12·d)
 *   07.4 E2  g_θ = ln(K_θ ÷ K0)
 *   07.4 E3  Π_θ = π·T0·w·g_θ
 *   07.4 E4  effect.fee = (Π_digitalRetail + Π_commercialTm) ÷ F0; effect.efficiency = (Π_efficiencyData + Π_riskCompliance) ÷ ΣL0;
 *            effect.retention = g_digitalRetail; effect.service = g_riskCompliance
 * d is the monthly decay from δ by README §4 rule (c). Spend arrives in SimStart dollars (07.3 E4 scales the expense by σ).
 */
import { InvariantViolation, roundToUsd, TechTheme, type BankState, type CapabilityModel, type CapabilityParams, type Usd } from "@bsim/domain";

const THEME_WEIGHT = 1 / TechTheme.options.length; // w: Balanced is 25 × 4 (expander tech.balancedWeightRate)

/** K0 = T0·w ÷ (12·d): the reference stock, d the monthly decay of the annual δ. */
const referenceStockUsd = (t0: number, decayAnnualRate: number): number => (t0 * THEME_WEIGHT) / 12 / (1 - (1 - decayAnnualRate) ** (1 / 12));

/**
 * cap_i = ¼ Σ_θ ln(K_θ ÷ K0) (07.4 E5) as of the bank's stocks. A theme without a stock counts as the reference (g = 0), so a
 * SimStart bank is 0; 0 too while the ref carries no T0.
 */
export function capabilityIndex(bank: BankState, params: CapabilityParams): number {
  const { decayAnnualRate, referenceSpendAnnualUsd: t0 } = params;
  if (t0 === undefined) return 0;
  const k0 = referenceStockUsd(t0, decayAnnualRate);
  let g = 0;
  for (const theme of TechTheme.options) g += Math.log((bank.franchise.techStockUsdByTheme[theme] ?? k0) / k0);
  return g / TechTheme.options.length;
}

/**
 * What the bank's capability stocks earn in a year (07.4 E3-E4, the dollars before they are divided by F0 and ΣL0): Π of the fee themes (digital retail, commercial treasury) and of the efficiency themes
 * (efficiency and data, risk and compliance), at the bank's stocks as they stand; a theme without a stock earns nothing (the reference). 0 while the ref carries no T0.
 */
export function capabilityReturnsAnnualUsd(bank: BankState, params: CapabilityParams): { readonly feeUsd: number; readonly efficiencyUsd: number } {
  const { decayAnnualRate, elasticity, referenceSpendAnnualUsd: t0 } = params;
  if (t0 === undefined) return { feeUsd: 0, efficiencyUsd: 0 };
  const k0 = referenceStockUsd(t0, decayAnnualRate);
  const returnUsd = (theme: TechTheme): number => elasticity * t0 * THEME_WEIGHT * Math.log((bank.franchise.techStockUsdByTheme[theme] ?? k0) / k0);
  return { feeUsd: returnUsd("digitalRetail") + returnUsd("commercialTm"), efficiencyUsd: returnUsd("efficiencyData") + returnUsd("riskCompliance") };
}

export const capabilityStock: CapabilityModel = {
  kind: "capability",
  update(call, { bank, spendUsdByTheme }) {
    const { lagMonths, decayAnnualRate, elasticity, referenceSpendAnnualUsd: t0, feeBaseAnnualUsd, efficiencyBaseAnnualUsd } = call.ref.params;
    if (t0 === undefined) throw new InvariantViolation("07.4", "capability ref lacks referenceSpendAnnualUsd");
    const decay = 1 - (1 - decayAnnualRate) ** (1 / 12);
    const referenceMonthlyUsd = (t0 * THEME_WEIGHT) / 12;
    const referenceStock = referenceStockUsd(t0, decayAnnualRate);
    const history = bank.franchise.techSpendHistoryUsd;
    const lagIndex = history.length - lagMonths;

    const stockUsdByTheme: Partial<Record<TechTheme, Usd>> = {};
    const g = {} as Record<TechTheme, number>;
    for (const theme of TechTheme.options) {
      const lagged = lagIndex < 0 ? undefined : lagIndex < history.length ? history[lagIndex]?.[theme] : (spendUsdByTheme[theme] ?? t0 * THEME_WEIGHT) / 12;
      const stock = (1 - decay) * (bank.franchise.techStockUsdByTheme[theme] ?? referenceStock) + (lagged ?? referenceMonthlyUsd);
      stockUsdByTheme[theme] = roundToUsd(stock);
      g[theme] = Math.log(stock / referenceStock);
    }
    const returnUsd = (theme: TechTheme): number => elasticity * t0 * THEME_WEIGHT * g[theme];
    return {
      stockUsdByTheme,
      effect: {
        retention: g.digitalRetail,
        fee: (returnUsd("digitalRetail") + returnUsd("commercialTm")) / feeBaseAnnualUsd,
        efficiency: (returnUsd("efficiencyData") + returnUsd("riskCompliance")) / efficiencyBaseAnnualUsd,
        service: g.riskCompliance,
      },
    };
  },
};
